/**
 * Nodes — Catalog of all available workflow node types
 *
 * Three-panel layout: Category sidebar (left) + Node grid (center) + Detail panel (right).
 * Each node shows: description, input/output schema, configuration options, usage stats.
 * "Add to Workflow" button navigates to builder.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Blocks, Search, Zap, Bot, Plug, GitBranch, GitMerge, Shield,
  ArrowRight, ChevronDown, X, Code, Workflow, Variable,
  Sparkles, Clock, Globe, Bell, Repeat, ShieldCheck,
  ArrowDown, ArrowUp, Settings2, BookOpen, Info, Plus,
  Radio,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  NODE_TYPE_CONFIG,
  getNodeRuntimeSupport,
  type NodeType,
} from '@/lib/mock-automate-data';
import { apiClient } from '@/lib/api-client';
import type { LucideIcon } from 'lucide-react';

// R3 P1 #9 (fix-automate-r3-p1) — replace hardcoded `usageCount`
// literals in NODE_META with real-data aggregates from
// /automate-workflows/node-usage-stats. The catalog still ships
// design-time metadata (description, inputs, outputs, config options)
// hardcoded — those are descriptive, not telemetry. Only the
// "X uses" badge + the "Used in workflows" tile read from the API.
interface NodeUsageStatsResponse {
  stats: Record<string, number>;
}
function useNodeUsageStats() {
  return useQuery<NodeUsageStatsResponse, Error>({
    queryKey: ['automate-workflows', 'node-usage-stats'],
    queryFn: () =>
      apiClient.get<NodeUsageStatsResponse>('/automate-workflows/node-usage-stats'),
    staleTime: 60_000,
  });
}

export const Route = createFileRoute('/automate/nodes')({
  component: NodeCatalogPage,
});

// ── Icon resolver ───────────────────────────────────────────────────────────

const ICON_MAP: Record<string, LucideIcon> = {
  zap: Zap, bot: Bot, plug: Plug, 'git-branch': GitBranch, 'git-merge': GitMerge,
  sparkles: Sparkles, 'shield-check': ShieldCheck, repeat: Repeat,
  clock: Clock, globe: Globe, bell: Bell, search: Search,
  variable: Variable, code: Code, workflow: Workflow, blocks: Blocks,
  radio: Radio,
};

// ── Node metadata (descriptions, inputs, outputs, config, examples) ─────────
//
// ACCEPTABLE_HARDCODED — Static catalog of supported node types.
// These are design tokens for the visual workflow builder, NOT tenant data.
// Each entry describes a node type the canvas/runtime supports (trigger, agent,
// condition, etc.). The backend stores `node_kind` as a free-form string
// (see packages/api/src/routes/v1/automate-workflows.ts) and accepts any kind
// the UI defines — so this catalog must stay in sync with `NODE_TYPE_CONFIG`
// in `lib/mock-automate-data.ts` and the runtime executor's supported kinds.
//
// `usageCount` here is illustrative copy for the catalog detail panel only;
// real per-tenant usage stats would come from a future endpoint and are not
// surfaced anywhere outside this read-only descriptive panel.
//
// Do NOT replace with an API call: tenants cannot define new node types from
// the UI — that requires a code change to add a new node implementation. If
// custom node-type extensibility is ever added, this must be wired to the
// backend node-kind registry.

interface NodeMeta {
  description: string;
  longDescription: string;
  inputs: { name: string; type: string; description: string }[];
  outputs: { name: string; type: string; description: string }[];
  configOptions: string[];
  usageExample: string;
  usageCount: number;
  tags: string[];
}

const NODE_META: Partial<Record<NodeType, NodeMeta>> = {
  trigger: {
    description: 'Start workflows with schedules, events, webhooks, or manual triggers',
    longDescription: 'The entry point for every workflow. Triggers determine when and how a workflow starts executing. Schedule triggers use cron expressions for time-based execution. Event triggers react to system events like alarms or threshold breaches. Webhook triggers expose an HTTP endpoint. Manual triggers are started by users from the dashboard.',
    inputs: [],
    outputs: [{ name: 'trigger_data', type: 'object', description: 'Event payload, schedule metadata, or webhook body' }],
    configOptions: ['Trigger type (schedule/event/webhook/manual)', 'Cron expression with presets', 'Event type selector', 'Webhook URL (auto-generated)'],
    usageExample: 'Trigger: Cron "0 6 * * *" → runs daily at 6 AM',
    usageCount: 11, tags: ['entry-point', 'cron', 'event', 'webhook'],
  },
  agent: {
    description: 'Execute an AI agent with tools, skills, memory, and knowledge access',
    longDescription: 'The most powerful node type. Configures a full AI agent inline within the workflow — including LLM model, tools from the MCP catalog, skills, knowledge base access, memory settings, and approval policies. Opens a dedicated configuration sheet with searchable tool/skill/knowledge pickers. The agent reasons autonomously within its step, calling tools as needed.',
    inputs: [{ name: 'context', type: 'object', description: 'Input data and instructions from previous steps' }],
    outputs: [{ name: 'result', type: 'object', description: 'Agent response, tool call results, reasoning trace' }],
    configOptions: ['Agent source (inline or pre-built)', 'Instructions/system prompt', 'Model selection (72B/3B/32B/128K/1M)', 'Tools (searchable, 20+ available)', 'Skills (searchable, 8 available)', 'Knowledge sources (folders, documents, upload)', 'Context window allocation', 'Max steps, temperature', 'Memory settings', 'Approval policy', 'Output format'],
    usageExample: 'Agent: Maintenance Intelligence → analyze vibration data using 5 tools, 2 skills, full library access',
    usageCount: 14, tags: ['ai', 'llm', 'tools', 'reasoning', 'autonomous'],
  },
  mcp_tool: {
    description: 'Call any MCP tool from connected servers (IQ, SAP, SCADA, etc.)',
    longDescription: 'Direct execution of a single MCP tool without AI reasoning. Select a connection (IQ Native, External, Forge), then pick a specific tool and configure its parameters. Useful for deterministic data fetching steps where AI reasoning is unnecessary.',
    inputs: [{ name: 'parameters', type: 'object', description: 'Tool input parameters (can reference upstream variables)' }],
    outputs: [{ name: 'result', type: 'object', description: 'Tool execution result (JSON)' }],
    configOptions: ['Connection source (IQ/External/Forge)', 'Tool selector (filtered by connection)', 'Parameters key-value editor', 'Timeout (seconds)'],
    usageExample: 'Tool: iq.telemetry.getHistory → get 30-day vibration trend for P-401',
    usageCount: 8, tags: ['mcp', 'api', 'data-fetch', 'deterministic'],
  },
  condition: {
    description: 'Branch workflow based on rules — if/else with field, operator, value',
    longDescription: 'Rule-based branching (deterministic, no AI). Evaluates a condition against data from previous steps and routes to either the "true" or "false" output path. Supports operators: equals, greater than, less than, contains, regex match, exists.',
    inputs: [{ name: 'data', type: 'object', description: 'Data to evaluate the condition against' }],
    outputs: [
      { name: 'true', type: 'object', description: 'Data forwarded when condition is TRUE' },
      { name: 'false', type: 'object', description: 'Data forwarded when condition is FALSE' },
    ],
    configOptions: ['Variable path (e.g., severity)', 'Operator (eq/gt/lt/contains/regex/exists)', 'Value to compare against', 'Compound conditions (AND/OR)'],
    usageExample: 'Condition: if severity >= "high" → route to supervisor approval, else → send report',
    usageCount: 4, tags: ['branching', 'if-else', 'routing', 'deterministic'],
  },
  ai_router: {
    description: 'LLM-based intent classification — the AI decides which path to take',
    longDescription: 'Uses an LLM to classify input and route to different branches based on the classification result. Unlike the deterministic Condition node, the AI Router can understand natural language and make nuanced routing decisions. Each category becomes an output handle.',
    inputs: [{ name: 'input', type: 'string | object', description: 'Text or data for the LLM to classify' }],
    outputs: [{ name: 'category_N', type: 'object', description: 'One output per classification category' }],
    configOptions: ['Classification prompt', 'Category list (dynamic, add/remove)', 'Model selector', 'Confidence threshold'],
    usageExample: 'AI Router: "Is this a maintenance, safety, or compliance issue?" → routes to specialist agents',
    usageCount: 2, tags: ['ai', 'classification', 'routing', 'intent'],
  },
  approval: {
    description: 'Pause for human approval — single confirm, supervisor, or 2-person rule',
    longDescription: 'Creates an approval request in the Approvals system and pauses the workflow until a human acts. Supports three approval levels: single confirm (any authorized user), supervisor (requires manager), and 2-person rule (requires two independent approvals). Connects to ArgusAI\'s tiered approval system.',
    inputs: [{ name: 'request', type: 'object', description: 'Data to show in the approval card' }],
    outputs: [
      { name: 'approved', type: 'object', description: 'Original data forwarded on approval' },
      { name: 'rejected', type: 'object', description: 'Data + rejection reason on reject' },
    ],
    configOptions: ['Approval level (single/supervisor/2-person)', 'Approver (user or role)', 'Timeout (minutes)', 'Auto-approve threshold for low risk'],
    usageExample: 'Approval: Supervisor must approve before work order is created in CMMS',
    usageCount: 3, tags: ['human-in-the-loop', 'governance', 'safety'],
  },
  loop: {
    description: 'Iterate over arrays, repeat N times, or loop until a condition is met',
    longDescription: 'Three loop modes: Iterate (process each item in an array), Repeat (run N times), and While (repeat until a condition is true). The "body" output connects to the steps that run inside the loop. The "done" output fires after the loop completes.',
    inputs: [{ name: 'collection', type: 'array | number', description: 'Array to iterate or count to repeat' }],
    outputs: [
      { name: 'body', type: 'object', description: 'Current item (per iteration)' },
      { name: 'done', type: 'array', description: 'All accumulated results after loop completes' },
    ],
    configOptions: ['Loop type (iterate/repeat/while)', 'Collection expression', 'Batch size', 'Max iterations', 'Parallel toggle'],
    usageExample: 'Loop: For each overdue work order → check equipment health → rank by risk',
    usageCount: 2, tags: ['iteration', 'batch', 'array-processing'],
  },
  delay: {
    description: 'Wait for a specified duration or until a specific time',
    longDescription: 'Pauses workflow execution for a configured amount of time. Useful for rate limiting, waiting for external processes to complete, or scheduling delayed actions. The workflow resumes automatically after the delay.',
    inputs: [{ name: 'data', type: 'object', description: 'Pass-through data (unchanged)' }],
    outputs: [{ name: 'data', type: 'object', description: 'Same data, forwarded after the delay' }],
    configOptions: ['Duration + unit (seconds/minutes/hours/days)', 'Or: specific datetime'],
    usageExample: 'Delay: Wait 5 minutes after sending notification before checking response',
    usageCount: 1, tags: ['timing', 'wait', 'rate-limit'],
  },
  webhook: {
    description: 'Make HTTP requests to external APIs (GET, POST, PUT, DELETE)',
    longDescription: 'Send outbound HTTP requests to any REST API endpoint. Configure method, URL, headers, body, and expected response format. Supports retry on failure. Useful for integrating with systems that don\'t have MCP connectors.',
    inputs: [{ name: 'request', type: 'object', description: 'Data to include in request body or URL parameters' }],
    outputs: [{ name: 'response', type: 'object', description: 'HTTP response (status, headers, body)' }],
    configOptions: ['URL', 'Method (GET/POST/PUT/DELETE)', 'Headers (key-value)', 'Body template', 'Expected status code', 'Retry count'],
    usageExample: 'HTTP: POST to ServiceNow API → create incident ticket with workflow data',
    usageCount: 3, tags: ['http', 'rest', 'api', 'integration'],
  },
  notification: {
    description: 'Send messages via email, Slack, Teams, or SMS',
    longDescription: 'Deliver notifications through configured channels. Supports email (Outlook/SMTP), Slack (channel or DM), Microsoft Teams (webhook), and SMS (Twilio). Message templates can include variables from workflow data.',
    inputs: [{ name: 'data', type: 'object', description: 'Data to include in the notification message' }],
    outputs: [{ name: 'result', type: 'object', description: 'Delivery status (sent/failed)' }],
    configOptions: ['Channel (email/slack/teams/sms)', 'Recipients (dynamic list)', 'Subject line', 'Message template with {{variables}}'],
    usageExample: 'Notification: Email shift handover report to maintenance-team@company.com',
    usageCount: 5, tags: ['messaging', 'email', 'slack', 'alert'],
  },
  llm_generate: {
    description: 'Direct LLM call with prompt template, model selection, and output formatting',
    longDescription: 'Call an LLM directly without the full agent framework. Provide a prompt template with variables from previous steps, select a model, and get a generated response. Simpler and faster than an Agent node when you don\'t need tool calling or autonomous reasoning.',
    inputs: [{ name: 'variables', type: 'object', description: 'Data to inject into the prompt template' }],
    outputs: [{ name: 'generated', type: 'string | object', description: 'LLM response (text or structured JSON)' }],
    configOptions: ['Prompt template with {{variables}}', 'Model selection', 'Temperature', 'Max tokens', 'Output format (text/JSON/markdown)'],
    usageExample: 'LLM: Generate executive summary from collected data using precise model',
    usageCount: 3, tags: ['llm', 'generation', 'prompt', 'text'],
  },
  rag_search: {
    description: 'Search knowledge base with hybrid retrieval, configurable top-K and scoring',
    longDescription: 'Execute a RAG search across the document library. Supports three search modes: Hybrid (dense embeddings + keyword), Dense only, and Sparse (keyword) only. Configure how many chunks to retrieve and the minimum relevance score. Results include document references with page/section citations.',
    inputs: [{ name: 'query', type: 'string', description: 'Search query (can use variables from previous steps)' }],
    outputs: [{ name: 'chunks', type: 'array', description: 'Retrieved document chunks with scores, titles, and citations' }],
    configOptions: ['Search query template', 'Search mode (hybrid/dense/sparse)', 'Top K (1-20)', 'Min score threshold (0-1)', 'Knowledge scope'],
    usageExample: 'RAG: Search "bearing replacement procedure for P-401" → returns top 5 relevant chunks',
    usageCount: 4, tags: ['rag', 'retrieval', 'search', 'knowledge'],
  },
  set_variable: {
    description: 'Transform, map, or set data between workflow steps using expressions',
    longDescription: 'Manipulate data flowing through the workflow. Set new variables, transform existing data, filter arrays, or reshape objects using JavaScript expressions. Access previous step outputs via $input and $steps.',
    inputs: [{ name: 'data', type: 'object', description: 'Data from previous step' }],
    outputs: [{ name: 'data', type: 'object', description: 'Transformed data with new/modified variables' }],
    configOptions: ['Variable name', 'Transform expression (JavaScript)', 'Fallback value on error'],
    usageExample: 'Set Variable: Extract equipment IDs from alarm data → save as "affectedAssets" array',
    usageCount: 2, tags: ['transform', 'data', 'variable', 'expression'],
  },
  merge: {
    description: 'Combine outputs from parallel branches — append, join by key, or zip',
    longDescription: 'When a workflow has parallel branches (fan-out), the Merge node collects results from all branches and combines them into a single output. Four strategies: Append (concatenate all), Join by Key (match records), Zip (pair by position), First Non-Empty (use whichever branch has data).',
    inputs: [
      { name: 'input-1', type: 'array', description: 'Data from first branch' },
      { name: 'input-2', type: 'array', description: 'Data from second branch' },
    ],
    outputs: [{ name: 'merged', type: 'array', description: 'Combined data from all inputs' }],
    configOptions: ['Merge strategy (append/join/zip/first)', 'Join key (for join strategy)', 'Wait for all inputs toggle'],
    usageExample: 'Merge: Combine telemetry data + maintenance history → feed to analysis agent',
    usageCount: 2, tags: ['combine', 'parallel', 'fan-in', 'join'],
  },
  code: {
    description: 'Execute custom JavaScript or Python code for data transformation',
    longDescription: 'Write custom code that runs within the workflow. Access input data via $input, previous step outputs via $steps, and workflow variables via $vars. Code executes in a sandboxed environment with configurable timeout. Use for complex transformations that expressions can\'t handle.',
    inputs: [{ name: 'data', type: 'object', description: 'Data from previous step (available as $input)' }],
    outputs: [{ name: 'result', type: 'object', description: 'Return value from the code execution' }],
    configOptions: ['Language (JavaScript/Python)', 'Code editor', 'Timeout (seconds)'],
    usageExample: 'Code: Filter work orders by criticality, calculate risk scores, sort by priority',
    usageCount: 1, tags: ['code', 'javascript', 'python', 'custom'],
  },
  sub_workflow: {
    description: 'Invoke another workflow as a step — pass parameters, wait or fire-and-forget',
    longDescription: 'Compose workflows by calling one workflow from another. Pass input parameters and receive results. Synchronous mode waits for the sub-workflow to complete before proceeding. Asynchronous mode fires the sub-workflow and continues immediately. Enables modular, reusable workflow design.',
    inputs: [{ name: 'parameters', type: 'object', description: 'Input parameters to pass to the sub-workflow' }],
    outputs: [{ name: 'result', type: 'object', description: 'Sub-workflow execution result (sync mode only)' }],
    configOptions: ['Workflow selector', 'Input parameters (JSON)', 'Execution mode (sync/async)'],
    usageExample: 'Sub-Workflow: Run "Document Onboarding" pipeline for each uploaded file',
    usageCount: 1, tags: ['composition', 'modular', 'reusable', 'sub-process'],
  },
  switch: {
    description: 'N-way branching — route to different paths based on a value or expression',
    longDescription: 'Like a multi-way if/else. Evaluates a field value against multiple cases and routes data to the matching output. Supports string matching, number ranges, and regex patterns. Each case becomes a separate output handle. Includes a default output for unmatched values.',
    inputs: [{ name: 'data', type: 'object', description: 'Data containing the field to switch on' }],
    outputs: [{ name: 'case_N', type: 'object', description: 'One output per case + optional default' }],
    configOptions: ['Switch field path', 'Cases (dynamic list)', 'Default output toggle'],
    usageExample: 'Switch: Route by department → maintenance | safety | compliance | other',
    usageCount: 0, tags: ['branching', 'routing', 'multi-way', 'switch-case'],
  },
  filter: {
    description: 'Keep items matching a condition, discard the rest',
    longDescription: 'Filters an array of items by evaluating a condition on each item. Items that match pass through; items that don\'t are discarded (or optionally sent to a second output). Supports field comparisons, existence checks, and regex patterns.',
    inputs: [{ name: 'items', type: 'array', description: 'Array of items to filter' }],
    outputs: [{ name: 'matched', type: 'array', description: 'Items that match the condition' }],
    configOptions: ['Filter field', 'Operator (eq/neq/contains/gt/lt/exists/regex)', 'Value', 'Split output for non-matching'],
    usageExample: 'Filter: Keep only work orders with status = "overdue"',
    usageCount: 0, tags: ['filter', 'array', 'condition', 'data-cleanup'],
  },
  aggregate: {
    description: 'Reduce multiple items to a single value — count, sum, average, etc.',
    longDescription: 'Takes an array of items and produces a single aggregated result. Operations include count, sum, average, min, max, first, last, and concatenate. Optionally group by a field to produce multiple aggregated results.',
    inputs: [{ name: 'items', type: 'array', description: 'Array of items to aggregate' }],
    outputs: [{ name: 'result', type: 'object', description: 'Aggregated value(s)' }],
    configOptions: ['Operation (count/sum/avg/min/max/first/last/concat)', 'Field to aggregate', 'Group by field (optional)'],
    usageExample: 'Aggregate: Count work orders by department → {maintenance: 12, safety: 5}',
    usageCount: 0, tags: ['aggregate', 'reduce', 'statistics', 'summary'],
  },
  split_out: {
    description: 'Split an array field into individual items for processing',
    longDescription: 'Takes an item containing an array field and outputs each array element as a separate item. The inverse of Aggregate. Useful for processing each element in a collection individually through subsequent nodes.',
    inputs: [{ name: 'data', type: 'object', description: 'Object containing an array field' }],
    outputs: [{ name: 'items', type: 'object', description: 'Individual items from the array (one per output)' }],
    configOptions: ['Array field path', 'Include parent fields toggle'],
    usageExample: 'Split Out: alarm_list → process each alarm individually through diagnosis agent',
    usageCount: 0, tags: ['split', 'fan-out', 'array', 'iterate'],
  },
  sort: {
    description: 'Sort items by a field in ascending or descending order',
    longDescription: 'Reorders an array of items based on the value of a specified field. Supports ascending and descending order. Useful for prioritizing results before display or processing.',
    inputs: [{ name: 'items', type: 'array', description: 'Array of items to sort' }],
    outputs: [{ name: 'sorted', type: 'array', description: 'Sorted array' }],
    configOptions: ['Sort field', 'Direction (ascending/descending)'],
    usageExample: 'Sort: Order equipment by risk score descending → highest risk first',
    usageCount: 0, tags: ['sort', 'order', 'ranking'],
  },
  limit: {
    description: 'Take the first or last N items from a collection',
    longDescription: 'Truncates an array to a specified number of items. Can take from the start (first N) or end (last N). Useful for pagination, top-N results, or sampling.',
    inputs: [{ name: 'items', type: 'array', description: 'Array of items' }],
    outputs: [{ name: 'limited', type: 'array', description: 'Truncated array' }],
    configOptions: ['Count (N)', 'From (first/last)'],
    usageExample: 'Limit: Take top 5 highest-risk equipment from sorted list',
    usageCount: 0, tags: ['limit', 'truncate', 'top-n', 'pagination'],
  },
  remove_duplicates: {
    description: 'Remove duplicate items based on a key field',
    longDescription: 'Deduplicates an array by comparing a specified field. Keeps either the first or last occurrence of each unique value. Essential for data cleanup when merging from multiple sources.',
    inputs: [{ name: 'items', type: 'array', description: 'Array with potential duplicates' }],
    outputs: [{ name: 'unique', type: 'array', description: 'Deduplicated array' }],
    configOptions: ['Deduplicate by field', 'Keep (first/last occurrence)'],
    usageExample: 'Remove Duplicates: Dedupe merged alarm list by alarm_id',
    usageCount: 0, tags: ['deduplicate', 'unique', 'data-cleanup'],
  },
  error_handler: {
    description: 'Catch errors from workflow nodes and define recovery behavior',
    longDescription: 'Intercepts errors thrown by other nodes in the workflow. Can catch errors from any node or a specific node. Supports multiple recovery strategies: continue with error data, retry the failed node, run a fallback workflow, or notify and stop.',
    inputs: [{ name: 'error', type: 'object', description: 'Error details from failed node' }],
    outputs: [{ name: 'recovery', type: 'object', description: 'Error data for recovery processing' }],
    configOptions: ['Error source (any/specific)', 'Recovery action (continue/retry/fallback/notify)', 'Max retries'],
    usageExample: 'Error Handler: If MCP tool fails → retry 3x with backoff → notify on persistent failure',
    usageCount: 0, tags: ['error', 'recovery', 'retry', 'resilience'],
  },
  stop: {
    description: 'Halt workflow execution with a success or error status and message',
    longDescription: 'Terminates the workflow explicitly. Can stop with success (workflow completed its purpose) or error (workflow failed with a message). Useful at the end of branches or as an error termination point.',
    inputs: [{ name: 'data', type: 'object', description: 'Final data at stop point' }],
    outputs: [],
    configOptions: ['Stop mode (success/error)', 'Message'],
    usageExample: 'Stop: If no issues found → stop with success "No action needed"',
    usageCount: 0, tags: ['stop', 'end', 'terminate', 'exit'],
  },
  wait_for_event: {
    description: 'Pause workflow until an external event or signal is received',
    longDescription: 'Suspends workflow execution and waits for an external signal before continuing. Unlike Delay (time-based), this waits for a specific event: webhook callback, system event, approval response, or external API signal. Configurable timeout with fallback behavior.',
    inputs: [{ name: 'context', type: 'object', description: 'Data to preserve during wait' }],
    outputs: [{ name: 'event', type: 'object', description: 'Received event data' }],
    configOptions: ['Wait type (webhook/event/approval/external)', 'Timeout (minutes)', 'On timeout (fail/continue/default)'],
    usageExample: 'Wait: After sending PO → wait for vendor confirmation webhook → continue processing',
    usageCount: 0, tags: ['wait', 'suspend', 'external', 'callback'],
  },
  wait_for_condition: {
    description: 'Poll upstream output until a field condition is met (or time out)',
    longDescription: 'Suspends execution and re-checks a field on the previous step\'s output against a comparison rule on a fixed interval until the condition is satisfied or the deadline is reached. Unlike Wait for Event (external signal) or Delay (fixed time), this is a data-driven gate over the in-flight workflow output. Poll interval is clamped to 1–60s and the total wait to a 5-minute engine maximum; a timeout surfaces a structured error with the attempt count and elapsed time.',
    inputs: [{ name: 'upstream', type: 'object', description: 'Previous step output to evaluate the condition against' }],
    outputs: [{ name: 'result', type: 'object', description: 'satisfied flag, attempts, and elapsed time once the condition is met' }],
    configOptions: ['Field (path on upstream output)', 'Operator (eq/neq/gt/lt/…)', 'Value', 'Value type', 'Poll interval (ms, 1s–60s)', 'Timeout (ms, up to 5 min)'],
    usageExample: 'Wait for Condition: poll status field until status == "ready" → continue',
    usageCount: 0, tags: ['wait', 'poll', 'condition', 'gate'],
  },
  emit_event: {
    description: 'Emit an internal event to the workflow event outbox',
    longDescription: 'Writes a tenant-scoped workflow event that other platform components can consume. Use this for handoffs between workflows, audit-friendly automation signals, and downstream event-triggered jobs.',
    inputs: [{ name: 'payload', type: 'object', description: 'Event payload assembled from previous step outputs' }],
    outputs: [{ name: 'event', type: 'object', description: 'Persisted event id, type, and dispatch status' }],
    configOptions: ['Event type', 'Payload template', 'Correlation key', 'Dispatch policy'],
    usageExample: 'Emit Event: workflow.customer_po.ready -> continue in downstream workflow',
    usageCount: 0, tags: ['event', 'outbox', 'handoff', 'automation'],
  },
  // Five UI-only kinds (http_response / date_time / template /
  // parse_generate / workspace_handoff) were removed in
  // fix/automate-carryover-majors (round-7 NEW-MAJOR-3) — they had
  // no backend support. Re-add catalog entries on both sides
  // together when the kinds ship as real product features.
};

// ── Categories ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'Triggers', icon: Zap, description: 'How workflows start' },
  { id: 'AI & Agents', icon: Bot, description: 'AI agents, LLM, RAG, classification' },
  { id: 'Logic', icon: GitBranch, description: 'Control flow — branching, loops, waits' },
  { id: 'Data', icon: Blocks, description: 'Data manipulation — filter, sort, merge, aggregate' },
  { id: 'Transform', icon: Code, description: 'Code, templates, format conversion' },
  { id: 'Actions', icon: Plug, description: 'Tools, HTTP, notifications, handoffs' },
  { id: 'Governance', icon: Shield, description: 'Approvals, error handling, compliance' },
  { id: 'Automation', icon: Radio, description: 'Internal workflow events and handoffs' },
];

// ── Page ────────────────────────────────────────────────────────────────────

function NodeCatalogPage() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<NodeType | null>(null);
  const { data: usageStats } = useNodeUsageStats();

  const allNodes = useMemo(() => {
    return Object.entries(NODE_TYPE_CONFIG).map(([key, config]) => {
      const type = key as NodeType;
      const meta = NODE_META[type] ?? {
        description: `${config.label} metadata is not registered yet.`,
        longDescription: `${config.label} is present in the workflow node registry, but its catalog metadata has not been registered. Treat it as unavailable until the implementation and catalog are aligned.`,
        inputs: [],
        outputs: [],
        configOptions: [],
        usageExample: `${config.label}: not available`,
        usageCount: 0,
        tags: ['unregistered'],
      };
      return {
        type,
        ...config,
        meta,
        runtime: getNodeRuntimeSupport(type),
        // R3 P1 #9 — real usage count from /automate-workflows/node-usage-stats.
        // Falls back to 0 when the API call is still loading or returns
        // no row for this kind — no more hardcoded literal counts.
        realUsageCount: usageStats?.stats[key] ?? 0,
      };
    });
  }, [usageStats]);

  const filtered = useMemo(() => {
    let nodes = allNodes;
    if (selectedCategory) nodes = nodes.filter(n => n.category === selectedCategory);
    if (search) {
      const q = search.toLowerCase();
      nodes = nodes.filter(n => n.label.toLowerCase().includes(q) || n.meta.description.toLowerCase().includes(q) || n.meta.tags.some(t => t.includes(q)));
    }
    return nodes;
  }, [allNodes, selectedCategory, search]);

  const grouped = useMemo(() => {
    const groups: Record<string, typeof filtered> = {};
    for (const node of filtered) {
      if (!groups[node.category]) groups[node.category] = [];
      groups[node.category].push(node);
    }
    return groups;
  }, [filtered]);

  const selectedNodeData = selectedNode ? allNodes.find(n => n.type === selectedNode) : null;
  const NodeIcon = selectedNodeData ? (ICON_MAP[selectedNodeData.icon] ?? Blocks) : null;

  return (
    <div className="flex h-full">
      {/* Category sidebar */}
      <div className="w-48 border-r bg-card p-3 space-y-1 shrink-0">
        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">Categories</h3>
        <button className={cn('w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors', !selectedCategory ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')} onClick={() => setSelectedCategory(null)}>
          All Nodes ({allNodes.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = allNodes.filter(n => n.category === cat.id).length;
          const CatIcon = cat.icon;
          return (
            <button key={cat.id} className={cn('w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors flex items-center gap-1.5', selectedCategory === cat.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')} onClick={() => setSelectedCategory(cat.id)}>
              <CatIcon className="h-3 w-3 shrink-0" />
              <span className="flex-1 truncate">{cat.id}</span>
              <Badge variant="secondary" className="text-[8px] h-4">{count}</Badge>
            </button>
          );
        })}
      </div>

      {/* Node grid */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="p-4 pb-3 space-y-3 border-b shrink-0">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2"><Blocks className="h-5 w-5 text-primary" /> Node Catalog</h2>
            <p className="text-xs text-muted-foreground">Browse all {allNodes.length} available node types. Click a node to see details.</p>
          </div>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search nodes by name, description, or tag..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {Object.entries(grouped).map(([category, nodes]) => (
              <Collapsible key={category} defaultOpen>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-1 hover:text-foreground transition-colors">
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{category}</span>
                  <Badge variant="secondary" className="text-[8px]">{nodes.length}</Badge>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    {nodes.map(node => {
                      const NIcon = ICON_MAP[node.icon] ?? Blocks;
                      const isSelected = selectedNode === node.type;
                      return (
                        <button key={node.type} className={cn('text-left rounded-lg border p-3 transition-all hover:border-primary/30 flex items-start gap-3', isSelected && 'border-primary ring-1 ring-primary/20 bg-primary/5')} onClick={() => setSelectedNode(node.type)}>
                          <div className={cn('w-8 h-8 rounded-md flex items-center justify-center shrink-0 border-l-[3px]', node.borderColor, 'bg-muted/50')}>
                            <NIcon className={cn('h-4 w-4', node.color)} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-semibold">{node.label}</span>
                              {node.realUsageCount > 0 && <span className="text-[8px] text-muted-foreground">{node.realUsageCount} uses</span>}
                              {node.runtime.status !== 'ready' && (
                                <Badge variant="outline" className="text-[7px] h-4">
                                  {node.runtime.label}
                                </Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{node.meta.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <Blocks className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No nodes match your search</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Detail panel (right) */}
      {selectedNodeData && NodeIcon && (
        <div className="w-[340px] border-l bg-card flex flex-col shrink-0">
          {/* Detail header */}
          <div className="p-4 border-b flex items-start gap-3">
            <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border-l-4', selectedNodeData.borderColor, 'bg-muted/50')}>
              <NodeIcon className={cn('h-5 w-5', selectedNodeData.color)} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold">{selectedNodeData.label}</h3>
              <div className="flex flex-wrap gap-1 mt-0.5">
                <Badge variant="outline" className="text-[8px]">{selectedNodeData.category}</Badge>
                <Badge variant={selectedNodeData.runtime.status === 'ready' ? 'secondary' : 'outline'} className="text-[8px]">
                  {selectedNodeData.runtime.label}
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSelectedNode(null)} title="Close" aria-label="Close">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {/* Description */}
              <div>
                <p className="text-xs text-muted-foreground leading-relaxed">{selectedNodeData.meta.longDescription}</p>
                {selectedNodeData.runtime.status !== 'ready' && (
                  <p className="text-[10px] text-amber-800 dark:text-amber-300 mt-2">
                    {selectedNodeData.runtime.note}
                  </p>
                )}
              </div>

              <Separator />

              {/* Inputs */}
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ArrowDown className="h-3 w-3 text-emerald-500" /> Inputs</h4>
                {selectedNodeData.meta.inputs.length > 0 ? selectedNodeData.meta.inputs.map((inp, i) => (
                  <div key={i} className="bg-emerald-500/5 border border-emerald-500/10 rounded-md px-2.5 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-mono font-semibold text-emerald-600 dark:text-emerald-400">{inp.name}</span>
                      <Badge variant="secondary" className="text-[7px] h-3 px-1">{inp.type}</Badge>
                    </div>
                    <p className="text-[9px] text-muted-foreground mt-0.5">{inp.description}</p>
                  </div>
                )) : (
                  <p className="text-[10px] text-muted-foreground italic">No inputs — this is an entry point node</p>
                )}
              </div>

              {/* Outputs */}
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ArrowUp className="h-3 w-3 text-blue-700 dark:text-blue-300" /> Outputs</h4>
                {selectedNodeData.meta.outputs.map((out, i) => (
                  <div key={i} className="bg-blue-500/5 border border-blue-500/10 rounded-md px-2.5 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-mono font-semibold text-blue-700 dark:text-blue-300">{out.name}</span>
                      <Badge variant="secondary" className="text-[7px] h-3 px-1">{out.type}</Badge>
                    </div>
                    <p className="text-[9px] text-muted-foreground mt-0.5">{out.description}</p>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Configuration Options */}
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Settings2 className="h-3 w-3" /> Configuration</h4>
                <ul className="space-y-1">
                  {selectedNodeData.meta.configOptions.map((opt, i) => (
                    <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1.5">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{opt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Separator />

              {/* Usage Example */}
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><BookOpen className="h-3 w-3" /> Example</h4>
                <div className="bg-muted/50 rounded-md px-3 py-2">
                  <p className="text-[10px] font-mono text-muted-foreground">{selectedNodeData.meta.usageExample}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1">
                {selectedNodeData.meta.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-[8px]">{tag}</Badge>
                ))}
              </div>

              {/* Stats — R3 P1 #9: real count from /automate-workflows/node-usage-stats */}
              <div className="bg-muted/30 rounded-md px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Used in workflows:</span>
                <span className="text-xs font-semibold">{selectedNodeData.realUsageCount}</span>
              </div>
            </div>
          </ScrollArea>

          {/* Add to workflow — disabled honestly.
              We have no "stage a node + pick a workflow" flow yet. Pretending
              to do so by navigating away dropped users on /automate with no
              indication of state (#101). Users add nodes from inside the
              workflow editor's palette instead. */}
          <div className="p-3 border-t">
            <Button
              disabled
              className="w-full gap-1.5"
              size="sm"
              title="Open a workflow from /automate, then use Add Node from inside the editor"
            >
              <Plus className="h-3.5 w-3.5" /> Add to Workflow
            </Button>
            <p className="text-[10px] text-muted-foreground text-center mt-2 px-1">
              Open a workflow from <span className="font-medium">/automate</span>,
              then use <span className="font-medium">Add Node</span> from inside the editor.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
